import { Component, memo, useEffect, useState } from 'react';
import type { PropsWithChildren, ReactNode } from 'react';

import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import type { Options } from '@contentful/rich-text-react-renderer';
import { BLOCKS, helpers, INLINES } from '@contentful/rich-text-types';
import type {
  Block,
  Text as ContentfulText,
  Inline,
  Paragraph,
  Document as RichTextDocument,
} from '@contentful/rich-text-types';
import { Box, Col, Link, Text } from '@opendoor/bricks-next';
import { globalObservability } from '@opendoor/observability/slim';
import { isEqual } from 'es-toolkit';

import { COSMOS_URLS } from '../../components/globals';
import type { CONTENT_TYPE } from '../../declarations/contentful';
import type IEntryLookup from '../entries/entries';
import type { EntryComponent } from '../entries/entries';
import type { IIdToLoaderData } from '../loaders';
import renderAsset, { isAsset } from './assets';

const Sentry = globalObservability.getSentryClient();

export const getLinkForAnchorTagWithId = (input: string) => {
  let output = '';
  for (let i = 0; i < input.length; i++) {
    const curr = input[i];

    if (curr === '-') {
      output = output + '-';
      continue;
    }

    if (curr.match(/\s/)) {
      output = output + '-';
      continue;
    }
    if (curr.match(/\W/)) {
      continue;
    }
    output = output + curr.toLowerCase();
  }
  output = output.replace(/-$/, '');
  const id = output.replaceAll(/-+/g, '-');

  return `#${id}`;
};

class EntryErrorBoundary extends Component<
  PropsWithChildren<{ entry: unknown; loaderData: unknown }>,
  { hasError: boolean }
> {
  constructor(props: { entry: unknown; loaderData: unknown }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    // Update state so the next render will show the fallback UI.
    return { hasError: true };
  }

  override componentDidCatch(error: unknown, errorInfo: unknown) {
    Sentry.captureException?.(error, {
      captureContext: {
        extra: {
          errorInfo,
          entry: this.props.entry,
          loaderData: this.props.loaderData,
        },
      },
    });
  }

  override render(): ReactNode {
    if (this.state.hasError) {
      return null;
    }
    return this.props.children;
  }
}

/***
 * If you're not in prod and you add a "?dev" query param to your url it will
 * wrap each component and apply a label so you know exactly which component you're
 * currently looking at.
 */
const DevOverlay = ({
  contentTypeId,
  children,
}: {
  contentTypeId?: string;
  children: ReactNode;
}) => {
  const [showOverlay, setShowOverlay] = useState(false);

  useEffect(() => {
    setShowOverlay(new URLSearchParams(window.location.search).has('dev'));
  }, []);

  if (!showOverlay) {
    return children;
  }

  return (
    <div data-dev-overlay={contentTypeId} style={{ position: 'relative', display: 'flow-root' }}>
      <style>{`
        [data-dev-overlay]::before {
          content: attr(data-dev-overlay);
          position: absolute;
          top: 0;
          left: 0;
          background: salmon;
          color: white;
          font-size: 12px;
          padding: 2px 6px;
          border-bottom-right-radius: 4px;
          pointer-events: none;
          z-index: 9999;
        }
        [data-dev-overlay]::after {
          content: '';
          position: absolute;
          inset: 0;
          border: 2px solid salmon;
          pointer-events: none;
          z-index: 9999;
        }
      `}</style>
      {children}
    </div>
  );
};

type IRendererConstructor = Pick<RichTextRenderer, 'body' | 'idToLoaderData' | 'entryLookup'>;

type EmbeddedEntryBlockWithTargetId = Block & {
  nodeType: BLOCKS.EMBEDDED_ENTRY;
  data: { target: { sys: { id: string } } };
};

const hasEmbeddedEntryTargetId = (
  node: Block | Inline | ContentfulText,
): node is EmbeddedEntryBlockWithTargetId => {
  return node.nodeType === BLOCKS.EMBEDDED_ENTRY && typeof node.data?.target?.sys?.id === 'string';
};

const RendererComponent = memo(
  (props: IRendererConstructor) => {
    return new RichTextRenderer(props).render();
  },
  (prev, next) => {
    return Object.entries(prev).every(([k, v]) => {
      return isEqual(v, next[k as keyof typeof prev]);
    });
  },
);
RendererComponent.displayName = 'RichTextRenderer';

const isAllText = (
  content: Array<Block | Inline | ContentfulText>,
): content is Array<ContentfulText> => {
  return content.every((c) => helpers.isText(c));
};

class RichTextRenderer {
  body: RichTextDocument;
  idToLoaderData: IIdToLoaderData;
  entryLookup: IEntryLookup;
  options: Options;
  generatedIds: { [key: string]: number };
  firstBlockEntryId?: string;

  constructor({ body, idToLoaderData, entryLookup }: IRendererConstructor) {
    this.body = body;
    this.idToLoaderData = idToLoaderData;
    this.entryLookup = entryLookup;
    this.options = this.getOption();
    this.generatedIds = {};
    this.firstBlockEntryId = body.content?.find(hasEmbeddedEntryTargetId)?.data.target.sys.id;
  }

  generateId(content: Array<Block | Inline | ContentfulText>): string | null {
    if (!isAllText(content)) {
      return null;
    }
    // Type hint, in the "skipId" section, we verified there
    // are only
    const input = content.reduce((acc, curr) => {
      return acc + curr.value;
    }, '');
    let output = '';
    for (let i = 0; i < input.length; i++) {
      const curr = input[i];
      // replace any non word charager with an empty space
      // skip dashes with this
      if (curr === '-') {
        output = output + '-';
        continue;
      }
      // whitespace
      if (curr.match(/\s/)) {
        output = output + '-';
        continue;
      }
      if (curr.match(/\W/)) {
        continue;
      }
      output = output + curr.toLowerCase();
    }
    let id = output.replaceAll(/-+/g, '-');
    // handle duplicates - we'll just increase the appended
    // index if we have a duplicate id
    if (id in this.generatedIds) {
      this.generatedIds[id] += 1;
      id = `${id}-${this.generatedIds[id]}`;
    } else {
      this.generatedIds[id] = 0;
    }
    return id;
  }

  getOption(): Options {
    return {
      renderNode: {
        [BLOCKS.HEADING_1]: (node, children) => {
          const id = this.generateId(node.content);
          return (
            <Text
              tag="h1"
              id={id ?? ''}
              mt="$1"
              mb="$1"
              color="$contentPrimary"
              typography="$subheaderLarge"
              $largerThanMD={{ typography: '$headerMedium' }}
              $platform-web={{ scrollMarginTop: '80px' }}
            >
              {children}
            </Text>
          );
        },
        [BLOCKS.HEADING_2]: (node, children) => {
          const id = this.generateId(node.content);
          return (
            <Text
              tag="h2"
              id={id ?? ''}
              mt="$6"
              mb="$4"
              color="$contentPrimary"
              typography="$subheaderSmall"
              $largerThanMD={{ typography: '$subheaderLarge' }}
              $platform-web={{ scrollMarginTop: '80px' }}
            >
              {children}
            </Text>
          );
        },
        [BLOCKS.HEADING_3]: (node, children) => {
          const id = this.generateId(node.content);
          return (
            <Text
              tag="h3"
              id={id ?? ''}
              mt="$5"
              mb="$3"
              color="$contentPrimary"
              typography="$subheaderXsmall"
              $largerThanMD={{ typography: '$subheaderSmall' }}
              $platform-web={{ scrollMarginTop: '80px' }}
            >
              {children}
            </Text>
          );
        },
        [BLOCKS.QUOTE]: (_, children) => {
          return (
            <Col
              borderRadius="$4x"
              py="$3"
              px="$3"
              width="100%"
              backgroundColor="$backgroundTertiary"
              $largerThanMD={{ py: '$5', px: '$6' }}
            >
              {children}
            </Col>
          );
        },
        [BLOCKS.EMBEDDED_ENTRY]: (entryData) => {
          const entryComponent =
            this.entryLookup.block[
              entryData.data.target?.sys?.contentType?.sys?.id as CONTENT_TYPE
            ];
          if (!entryComponent) {
            return null;
          }
          return this.renderEntry(entryComponent, entryData);
        },
        [BLOCKS.EMBEDDED_ASSET]: (entryData) => {
          const fields = entryData.data.target.fields;
          if (!isAsset(fields)) {
            return null;
          }
          return renderAsset({
            src: fields.file.url,
            description: fields.description,
            contentType: fields.file.contentType,
            width: fields.file.details?.image?.width,
            height: fields.file.details?.image?.height,
          });
        },
        [INLINES.EMBEDDED_ENTRY]: (entryData) => {
          const entryComponent =
            this.entryLookup.inline[
              entryData.data.target?.sys?.contentType?.sys?.id as CONTENT_TYPE
            ];
          if (!entryComponent) {
            return null;
          }
          return this.renderEntry(entryComponent, entryData);
        },
        [BLOCKS.HEADING_4]: (node, children) => {
          const id = this.generateId(node.content);
          return (
            <Text
              tag="h4"
              id={id ?? ''}
              typography="$labelLarge"
              $largerThanMD={{ typography: '$subheaderSmall' }}
              $platform-web={{ scrollMarginTop: '80px' }}
            >
              {children}
            </Text>
          );
        },
        [BLOCKS.PARAGRAPH]: (entry: Block | Inline, children) => {
          const paragraphEntryContent = entry.content as Paragraph['content'];
          if (!Array.isArray(children)) {
            return (
              <Text
                tag="p"
                mb="$2"
                typography="$bodyMedium"
                $largerThanMD={{ typography: '$bodyLarge' }}
              >
                {children}
              </Text>
            );
          }

          // Don't render empty paragraph where the node only contains whitespace text
          const isEmptyParagraph =
            paragraphEntryContent.length === 1 &&
            paragraphEntryContent[0].nodeType == 'text' &&
            typeof paragraphEntryContent[0].value === 'string' &&
            paragraphEntryContent[0].value.trim().length === 0;
          if (isEmptyParagraph) {
            return null;
          }

          const zipped: Array<[Inline | ContentfulText, ReactNode]> = paragraphEntryContent.map(
            (e, i) => [e, children[i]],
          );
          const { inline, standard } = zipped.reduce<{
            inline: Array<ReactNode>;
            standard: Array<ReactNode>;
          }>(
            (acc, curr) => {
              const [entry, child] = curr;
              if (entry.nodeType === INLINES.EMBEDDED_ENTRY) {
                acc.inline = acc.inline.concat(child);
                return acc;
              }
              acc.standard = acc.standard.concat(child);
              return acc;
            },
            { inline: [], standard: [] },
          );
          // note: this re-orders the text components, i.e. if you have
          // test <inline entry> test2, it will be rendered as
          // <inline entry> test test2. This doesn't seem ideal, but works
          // for our use-case. We may want to disallow inline embedded entries
          // in the future, and just use the block type.
          return (
            <>
              {inline}
              <Text
                tag="p"
                my="$5"
                typography="$bodyMedium"
                $largerThanMD={{ typography: '$bodyLarge' }}
              >
                {standard}
              </Text>
            </>
          );
        },
        [INLINES.HYPERLINK]: (node, children) => {
          let target = '_blank';
          if (
            // https://opendoor.atlassian.net/jira/servicedesk/projects/ENGBUGS/queues/custom/1337/ENGBUGS-47459
            // we want all articles referenced in our articles to open in a new tab
            // we only add production urls to contentful
            !node.data.uri.startsWith(COSMOS_URLS['production'] + '/articles') &&
            (node.data.uri.startsWith('#') || node.data.uri.startsWith(COSMOS_URLS['production']))
          ) {
            target = '_self';
          }
          let hrefLink = node.data.uri;
          if (node.data.uri.startsWith('#') && node.content[0].nodeType === 'text') {
            const anchorHref = getLinkForAnchorTagWithId(node.content[0].value);
            hrefLink = anchorHref;
          }
          return (
            <Link
              href={hrefLink}
              analyticsName="cosmos-articles-renderer"
              aria-label=""
              target={target}
              typography="$bodyMedium"
              $largerThanMD={{ typography: '$bodyLarge' }}
            >
              {children}
            </Link>
          );
        },
        [BLOCKS.TABLE]: (_, children) => {
          return (
            <Box tag="table" $platform-web={{ display: 'table', borderCollapse: 'collapse' }}>
              <Box tag="tbody" $platform-web={{ display: 'table-row-group' }}>
                {children}
              </Box>
            </Box>
          );
        },
        [BLOCKS.TABLE_CELL]: (_, children) => {
          return (
            <Box
              tag="td"
              pr="$3"
              $largerThanSM={{ pr: '$4' }}
              $largerThanMD={{ pr: '$5' }}
              $platform-web={{ display: 'table-cell', textAlign: 'left', verticalAlign: 'middle' }}
            >
              {children}
            </Box>
          );
        },
        [BLOCKS.TABLE_ROW]: (_, children) => {
          return (
            <Box tag="tr" $platform-web={{ display: 'table-row' }}>
              {children}
            </Box>
          );
        },
        [BLOCKS.TABLE_HEADER_CELL]: (_, children) => {
          return (
            <Box
              tag="th"
              pr="$3"
              $largerThanSM={{ pr: '$4' }}
              $largerThanMD={{ pr: '$5' }}
              $platform-web={{ display: 'table-cell', textAlign: 'left', verticalAlign: 'middle' }}
            >
              {children}
            </Box>
          );
        },
      },
    };
  }

  /**
   * Render an entry safely, with Error Boundaries and safe server-side try-catch
   */
  renderEntry(entryComponent: EntryComponent, entryData: Block | Inline) {
    const entryId = entryData.data.target.sys.id;
    const loaderData = this.idToLoaderData[entryId];
    const { body, idToLoaderData } = this;
    let component: ReactNode | undefined;
    /**
     * Render the entry safely server-side
     */
    try {
      component = entryComponent.render(entryData.data.target, loaderData, {
        body,
        idToLoaderData,
        isFirstBlockEntry: entryId === this.firstBlockEntryId,
        entryLookup: this.entryLookup,
      });
    } catch (e) {
      globalObservability.getSentryClient().captureException?.(e, {
        data: {
          target: entryData.data.target,
          loaderData,
        },
      });
      return null;
    }

    const contentTypeId = entryData.data.target?.sys?.contentType?.sys?.id;

    return (
      <EntryErrorBoundary entry={entryData.data.target} loaderData={loaderData}>
        {process.env.NODE_ENV !== 'production' ? (
          <DevOverlay contentTypeId={contentTypeId}>{component}</DevOverlay>
        ) : (
          component
        )}
      </EntryErrorBoundary>
    );
  }

  /**
   * Custom memoized rich text renderer, only re-render it at a top-level if the body
   * input has changed (deep diff). Prevents re-renders if something at the top level
   * is misbehaving.
   */
  render() {
    return <>{documentToReactComponents(this.body, this.options)}</>;
  }

  static Component = RendererComponent;
}

export default RichTextRenderer;
